import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Type, Image as ImageIcon, Recycle, Trash2, ZoomIn, ZoomOut, Copy, Save, AlertTriangle, X } from 'lucide-react';
import apiService, { LabelTemplate } from '../../services/api';
import { Product } from '../../types';

// ---------------------------------------------------------------------------
// Modelo de elementos de la etiqueta. Coordenadas en mm desde la esquina
// superior izquierda de la etiqueta.
// ---------------------------------------------------------------------------
type Align = 'left' | 'center' | 'right';
type FontFamily = 'sans' | 'serif' | 'mono';

interface BaseEl {
  id: string;
  xMm: number;
  yMm: number;
}
interface TextEl extends BaseEl {
  type: 'text';
  text: string;
  wMm: number;
  fontPt: number;
  fontFamily?: FontFamily;
  bold: boolean;
  align: Align;
}
interface ImageEl extends BaseEl {
  type: 'image';
  src: string; // 'polcen' o data URL
  wMm: number;
  hMm: number;
  ratio: number; // w/h natural
  label: string;
}
interface RecycleEl extends BaseEl {
  type: 'recycle';
  code: string;
  wMm: number;
}
type SymbolIcon = 'fragile' | 'keepdry' | 'nostack' | 'thisway';
interface SymbolEl extends BaseEl {
  type: 'symbol';
  icon: SymbolIcon;
  wMm: number;
}
type LabelEl = TextEl | ImageEl | RecycleEl | SymbolEl;

const SYMBOL_META: Record<SymbolIcon, { label: string }> = {
  fragile: { label: 'Frágil' },
  keepdry: { label: 'Mantener seco' },
  nostack: { label: 'No apilar' },
  thisway: { label: 'Este lado arriba' }
};

const RESIN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'PET', label: '1 · PET' },
  { value: 'HDPE', label: '2 · HDPE (PEAD)' },
  { value: 'PVC', label: '3 · PVC' },
  { value: 'LDPE', label: '4 · LDPE (PEBD)' },
  { value: 'PP', label: '5 · PP' },
  { value: 'PS', label: '6 · PS' },
  { value: 'OTHER', label: '7 · Otros' }
];
const RESIN_NUMBER: Record<string, number> = { PET: 1, HDPE: 2, PVC: 3, LDPE: 4, PP: 5, PS: 6, OTHER: 7 };

const mapMaterialToResin = (material?: string): string => {
  const m = String(material || '').trim().toUpperCase();
  if (['PET', 'PETE'].includes(m)) return 'PET';
  if (['HDPE', 'PEAD', 'PE-HD'].includes(m)) return 'HDPE';
  if (m === 'PVC' || m === 'V') return 'PVC';
  if (['LDPE', 'PEBD', 'PE-LD', 'PE'].includes(m)) return 'LDPE';
  if (m === 'PP') return 'PP';
  if (m === 'PS') return 'PS';
  return m ? 'OTHER' : '';
};

// Logos de Polcen disponibles (mismas claves que el backend).
const POLCEN_LOGOS: Record<string, { url: string; ratio: number; label: string }> = {
  polcen: { url: '/images/logo-texto.jpg', ratio: 3.77, label: 'Logo Polcen' },
  'polcen-iso': { url: '/images/logo.jpg', ratio: 1.487, label: 'Isotipo Polcen' }
};
const logoSrc = (src: string) => POLCEN_LOGOS[src]?.url || src;

const uid = () => Math.random().toString(36).slice(2, 9);
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const round1 = (v: number) => Math.round(v * 10) / 10;
const PT_TO_MM = 25.4 / 72;
const FONT_FAMILY_CSS: Record<FontFamily, string> = {
  sans: 'Arial, Helvetica, sans-serif',
  serif: '"Times New Roman", Times, serif',
  mono: '"Courier New", Courier, monospace'
};

const normInt = (raw: string, fallback: number, min: number, max: number) => {
  const n = parseInt(String(raw ?? '').trim(), 10);
  return Number.isFinite(n) ? clamp(n, min, max) : fallback;
};

const defaultElements = (): LabelEl[] => [
  { id: uid(), type: 'text', text: 'Contiene', xMm: 4, yMm: 4, wMm: 55, fontPt: 11, bold: true, align: 'left' },
  { id: uid(), type: 'text', text: 'Bolsas', xMm: 4, yMm: 13, wMm: 55, fontPt: 15, bold: true, align: 'left' },
  { id: uid(), type: 'text', text: '90x115 x50u', xMm: 4, yMm: 24, wMm: 55, fontPt: 13, bold: false, align: 'left' },
  { id: uid(), type: 'image', src: 'polcen', xMm: 55, yMm: 4, wMm: 31, hMm: round1(31 / 3.77), ratio: 3.77, label: 'Logo Polcen' },
  { id: uid(), type: 'recycle', code: 'LDPE', xMm: 71, yMm: 23, wMm: 14 }
];

// ---------------------------------------------------------------------------
// Símbolo de reciclado de flechas gruesas, igual al que genera el PDF.
// ---------------------------------------------------------------------------
type Pt = [number, number];
const RECYCLE_ARROWS: Array<{ stem: Pt[]; head: [Pt, Pt, Pt] }> = [
  { stem: [[0.30, 0.39], [0.48, 0.10], [0.65, 0.39]], head: [[0.55, 0.36], [0.73, 0.52], [0.70, 0.29]] },
  { stem: [[0.76, 0.47], [0.90, 0.75], [0.59, 0.75]], head: [[0.62, 0.62], [0.40, 0.75], [0.62, 0.88]] },
  { stem: [[0.43, 0.84], [0.14, 0.84], [0.31, 0.55]], head: [[0.19, 0.57], [0.40, 0.39], [0.41, 0.64]] }
];

const RecycleMark: React.FC<{ code: string; px: number }> = ({ code, px }) => {
  const n = RESIN_NUMBER[code] ?? '';
  const S = 100;
  const point = ([x, y]: Pt) => `${x * S},${y * S}`;
  return (
    <svg viewBox={`0 0 ${S} ${S}`} width={px} height={px} style={{ display: 'block', overflow: 'visible' }}>
      {RECYCLE_ARROWS.map((arrow, i) => (
        <g key={i}>
          <polyline points={arrow.stem.map(point).join(' ')} fill="none" stroke="#111827" strokeWidth={S * 0.09} strokeLinecap="square" strokeLinejoin="round" />
          <polygon fill="#111827" points={arrow.head.map(point).join(' ')} />
        </g>
      ))}
      <text x={S * 0.5} y={S * 0.64} textAnchor="middle" fontSize={S * 0.34} fontWeight={800} fill="#111827">
        {n}
      </text>
      {code && (
        <text x={S * 0.5} y={S * 1.14} textAnchor="middle" fontSize={S * 0.22} fontWeight={800} fill="#111827">
          {code}
        </text>
      )}
    </svg>
  );
};

// Simbolos genericos de manipuleo. Trazos simples (lineas/poligonos/circulos)
// a proposito, para que sean faciles de reproducir en el backend con la misma
// tecnica que ya usa recycle_arrow_shapes.
const SymbolMark: React.FC<{ icon: SymbolIcon; px: number }> = ({ icon, px }) => {
  const S = 100;
  const stroke = '#111827';
  const sw = S * 0.05;
  let content: React.ReactNode;

  if (icon === 'fragile') {
    content = (
      <>
        <polygon
          points={`${S * 0.5},${S * 0.08} ${S * 0.05},${S * 0.92} ${S * 0.95},${S * 0.92}`}
          fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
        />
        <rect x={S * 0.46} y={S * 0.38} width={S * 0.08} height={S * 0.28} fill={stroke} />
        <circle cx={S * 0.5} cy={S * 0.78} r={S * 0.05} fill={stroke} />
      </>
    );
  } else if (icon === 'keepdry') {
    content = (
      <>
        <path d={`M ${S * 0.15} ${S * 0.5} A ${S * 0.35} ${S * 0.35} 0 0 1 ${S * 0.85} ${S * 0.5}`} fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1={S * 0.15} y1={S * 0.5} x2={S * 0.15} y2={S * 0.58} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <line x1={S * 0.85} y1={S * 0.5} x2={S * 0.85} y2={S * 0.58} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <line x1={S * 0.5} y1={S * 0.6} x2={S * 0.5} y2={S * 0.85} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <line x1={S * 0.12} y1={S * 0.15} x2={S * 0.88} y2={S * 0.88} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      </>
    );
  } else if (icon === 'nostack') {
    content = (
      <>
        <rect x={S * 0.25} y={S * 0.55} width={S * 0.5} height={S * 0.28} fill="none" stroke={stroke} strokeWidth={sw} />
        <rect x={S * 0.25} y={S * 0.17} width={S * 0.5} height={S * 0.28} fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1={S * 0.08} y1={S * 0.08} x2={S * 0.92} y2={S * 0.92} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      </>
    );
  } else {
    content = (
      <>
        <line x1={S * 0.5} y1={S * 0.85} x2={S * 0.5} y2={S * 0.18} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <polygon points={`${S * 0.5},${S * 0.05} ${S * 0.36},${S * 0.24} ${S * 0.64},${S * 0.24}`} fill={stroke} />
        <line x1={S * 0.22} y1={S * 0.85} x2={S * 0.22} y2={S * 0.2} stroke={stroke} strokeWidth={sw * 0.7} strokeLinecap="round" />
        <polygon points={`${S * 0.22},${S * 0.08} ${S * 0.13},${S * 0.22} ${S * 0.31},${S * 0.22}`} fill={stroke} />
        <line x1={S * 0.78} y1={S * 0.85} x2={S * 0.78} y2={S * 0.2} stroke={stroke} strokeWidth={sw * 0.7} strokeLinecap="round" />
        <polygon points={`${S * 0.78},${S * 0.08} ${S * 0.69},${S * 0.22} ${S * 0.87},${S * 0.22}`} fill={stroke} />
      </>
    );
  }

  return (
    <svg viewBox={`0 0 ${S} ${S}`} width={px} height={px} style={{ display: 'block', overflow: 'visible' }}>
      {content}
    </svg>
  );
};

// downscale de imagenes subidas para no mandar payloads gigantes
const fileToDataUrl = (file: File): Promise<{ src: string; ratio: number }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Imagen invalida'));
      img.onload = () => {
        const maxDim = 500;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas no disponible'));
        ctx.drawImage(img, 0, 0, w, h);
        const isPng = /\.png$/i.test(file.name) || file.type === 'image/png';
        resolve({
          src: canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', 0.85),
          ratio: w / h
        });
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

// tamano (px) de la caja de un elemento a una escala dada
const elBox = (el: LabelEl, pxPerMm: number): { width: number; height: number } => {
  if (el.type === 'text') {
    const fontPx = el.fontPt * PT_TO_MM * pxPerMm;
    return { width: el.wMm * pxPerMm, height: fontPx * 1.28 };
  }
  if (el.type === 'image') {
    return { width: el.wMm * pxPerMm, height: el.hMm * pxPerMm };
  }
  return { width: el.wMm * pxPerMm, height: el.wMm * pxPerMm };
};

// contenido visual de un elemento (sin interaccion)
const ElementInner: React.FC<{ el: LabelEl; pxPerMm: number }> = ({ el, pxPerMm }) => {
  if (el.type === 'text') {
    return (
      <span
        style={{
          fontSize: el.fontPt * PT_TO_MM * pxPerMm,
          fontFamily: FONT_FAMILY_CSS[el.fontFamily || 'sans'],
          fontWeight: el.bold ? 700 : 400,
          lineHeight: 1.1,
          width: '100%',
          textAlign: el.align,
          color: '#111827',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          display: 'block'
        }}
      >
        {el.text || ' '}
      </span>
    );
  }
  if (el.type === 'image') {
    return (
      <img
        src={logoSrc(el.src)}
        alt={el.label}
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
      />
    );
  }
  if (el.type === 'symbol') {
    return <SymbolMark icon={el.icon} px={el.wMm * pxPerMm} />;
  }
  return <RecycleMark code={el.code} px={el.wMm * pxPerMm} />;
};

// una etiqueta completa y estatica (para la vista previa de la hoja)
const StaticLabel: React.FC<{
  elements: LabelEl[];
  wMm: number;
  hMm: number;
  pxPerMm: number;
  border: boolean;
}> = ({ elements, wMm, hMm, pxPerMm, border }) => (
  <div
    style={{
      position: 'relative',
      width: wMm * pxPerMm,
      height: hMm * pxPerMm,
      background: '#fff',
      border: border ? '0.5px solid #0f172a' : '0.5px solid #e2e8f0',
      overflow: 'hidden'
    }}
  >
    {elements.map((el) => {
      const { width, height } = elBox(el, pxPerMm);
      return (
        <div
          key={el.id}
          style={{
            position: 'absolute',
            left: el.xMm * pxPerMm,
            top: el.yMm * pxPerMm,
            width,
            height,
            display: 'flex',
            alignItems: 'flex-start'
          }}
        >
          <ElementInner el={el} pxPerMm={pxPerMm} />
        </div>
      );
    })}
  </div>
);

// vista previa de la hoja A4 (misma disposicion que el backend)
const A4_W = 210;
const A4_H = 297;

const SheetPreview: React.FC<{
  elements: LabelEl[];
  wMm: number;
  hMm: number;
  quantity: number;
  border: boolean;
  cols: number;
  rows: number;
  marginMm: number;
  gapMm: number;
}> = ({ elements, wMm, hMm, quantity, border, cols, rows, marginMm, gapMm }) => {
  const pxPerMm = 340 / A4_W;
  const perPage = Math.max(1, cols * rows);
  const onThisPage = Math.min(quantity, perPage);
  const totalPages = Math.max(1, Math.ceil(quantity / perPage));

  // Centra la grilla dentro de la hoja para repartir el sobrante de espacio
  // en partes iguales arriba/abajo e izquierda/derecha (nunca por debajo del
  // margen minimo elegido).
  const gridW = cols * wMm + (cols - 1) * gapMm;
  const gridH = rows * hMm + (rows - 1) * gapMm;
  const offsetX = Math.max(marginMm, (A4_W - gridW) / 2);
  const offsetY = Math.max(marginMm, (A4_H - gridH) / 2);

  return (
    <div>
      <div
        style={{
          position: 'relative',
          width: A4_W * pxPerMm,
          height: A4_H * pxPerMm,
          margin: '0 auto',
          background: '#fff',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 8px rgba(15,23,42,0.12)'
        }}
      >
        {Array.from({ length: onThisPage }).map((_, i) => {
          const r = Math.floor(i / cols);
          const col = i % cols;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: (offsetX + col * (wMm + gapMm)) * pxPerMm,
                top: (offsetY + r * (hMm + gapMm)) * pxPerMm
              }}
            >
              <StaticLabel elements={elements} wMm={wMm} hMm={hMm} pxPerMm={pxPerMm} border={border} />
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-slate-400 mt-3">
        Hoja A4 · {onThisPage} etiqueta{onThisPage === 1 ? '' : 's'} en esta hoja
        {totalPages > 1 ? ` · ${totalPages} hojas en total` : ''}
      </p>
    </div>
  );
};

const Etiquetas: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const [labelWidthMm, setLabelWidthMm] = useState(90);
  const [labelHeightMm, setLabelHeightMm] = useState(45);
  const [quantity, setQuantity] = useState(27);
  const [showBorder, setShowBorder] = useState(true);
  const [marginMm, setMarginMm] = useState(8);
  const [gapMm, setGapMm] = useState(3);

  const [elements, setElements] = useState<LabelEl[]>(defaultElements);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [customLogos, setCustomLogos] = useState<Array<{ key: string; url: string; ratio: number; label: string }>>([]);
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);
  const [textEditorOpen, setTextEditorOpen] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{
    id: string;
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    orig: LabelEl;
  } | null>(null);

  useEffect(() => {
    apiService
      .getProducts({ includeInactive: true, limit: 500 })
      .then((res) => setProducts(res.products || []))
      .catch(() => setProducts([]));
  }, []);

  const loadTemplates = useCallback(() => {
    apiService
      .getLabelTemplates()
      .then((res) => setTemplates(res.templates || []))
      .catch(() => setTemplates([]));
  }, []);
  useEffect(() => loadTemplates(), [loadTemplates]);

  const pxPerMm = 3.4 * zoom;
  const selected = elements.find((e) => e.id === selectedId) || null;

  const patch = useCallback((id: string, updates: Partial<LabelEl>) => {
    setElements((prev) => prev.map((e) => (e.id === id ? ({ ...e, ...updates } as LabelEl) : e)));
  }, []);

  // --- drag / resize -------------------------------------------------------
  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dxMm = (e.clientX - d.startX) / pxPerMm;
      const dyMm = (e.clientY - d.startY) / pxPerMm;
      const o = d.orig;

      if (d.mode === 'move') {
        patch(d.id, {
          xMm: round1(clamp(o.xMm + dxMm, -5, labelWidthMm)),
          yMm: round1(clamp(o.yMm + dyMm, -5, labelHeightMm))
        });
        return;
      }
      if (o.type === 'text') {
        patch(d.id, {
          wMm: round1(clamp((o as TextEl).wMm + dxMm, 6, labelWidthMm + 10)),
          fontPt: clamp(Math.round((o as TextEl).fontPt + dyMm / PT_TO_MM), 4, 72)
        });
      } else if (o.type === 'image') {
        const img = o as ImageEl;
        const nextW = clamp(img.wMm + dxMm, 4, labelWidthMm + 10);
        patch(d.id, { wMm: round1(nextW), hMm: round1(nextW / (img.ratio || 1)) });
      } else {
        patch(d.id, { wMm: round1(clamp((o as RecycleEl).wMm + Math.max(dxMm, dyMm), 4, labelHeightMm + 10)) });
      }
    },
    [labelHeightMm, labelWidthMm, patch, pxPerMm]
  );

  const onPointerUp = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (
      drag?.mode === 'move' &&
      drag.orig.type === 'text' &&
      Math.abs(e.clientX - drag.startX) < 4 &&
      Math.abs(e.clientY - drag.startY) < 4
    ) {
      setTextEditorOpen(true);
    }
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onPointerDownEl = (e: React.PointerEvent, el: LabelEl, mode: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(el.id);
    dragRef.current = { id: el.id, mode, startX: e.clientX, startY: e.clientY, orig: el };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    },
    [onPointerMove, onPointerUp]
  );

  // --- add / remove elements --------------------------------------------------
  const addText = () => {
    const el: TextEl = { id: uid(), type: 'text', text: 'Texto nuevo', xMm: 4, yMm: 4, wMm: 50, fontPt: 12, fontFamily: 'sans', bold: false, align: 'left' };
    setElements((p) => [...p, el]);
    setSelectedId(el.id);
    setTextEditorOpen(true);
  };
  const addLogo = (key: 'polcen' | 'polcen-iso') => {
    const meta = POLCEN_LOGOS[key];
    const wMm = key === 'polcen' ? 32 : 16;
    const el: ImageEl = {
      id: uid(), type: 'image', src: key, xMm: 4, yMm: 4,
      wMm, hMm: round1(wMm / meta.ratio), ratio: meta.ratio, label: meta.label
    };
    setElements((p) => [...p, el]);
    setSelectedId(el.id);
  };
  const addRecycle = () => {
    const el: RecycleEl = { id: uid(), type: 'recycle', code: 'LDPE', xMm: 4, yMm: 4, wMm: 14 };
    setElements((p) => [...p, el]);
    setSelectedId(el.id);
  };
  const addSymbol = (icon: SymbolIcon) => {
    const el: SymbolEl = { id: uid(), type: 'symbol', icon, xMm: 4, yMm: 4, wMm: 16 };
    setElements((p) => [...p, el]);
    setSelectedId(el.id);
  };
  const onUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetId = replaceTargetId;
    e.target.value = '';
    setReplaceTargetId(null);
    if (!file) return;
    try {
      setError('');
      const { src, ratio } = await fileToDataUrl(file);
      if (targetId) {
        // reemplaza la imagen del elemento seleccionado, conservando su ancho
        const target = elements.find((el) => el.id === targetId);
        const wMm = target && target.type === 'image' ? target.wMm : 24;
        patch(targetId, { src, ratio, hMm: round1(wMm / ratio), label: file.name } as Partial<ImageEl>);
        setSelectedId(targetId);
      } else {
        const wMm = 24;
        const el: ImageEl = { id: uid(), type: 'image', src, xMm: 4, yMm: 4, wMm, hMm: round1(wMm / ratio), ratio, label: file.name };
        setElements((p) => [...p, el]);
        setSelectedId(el.id);
      }
      // la guarda como isotipo reutilizable para poder alternar entre logos despues
      setCustomLogos((prev) => (prev.some((l) => l.url === src) ? prev : [...prev, { key: src, url: src, ratio, label: file.name }].slice(-8)));
    } catch (err: any) {
      setError(err?.message || 'No se pudo cargar la imagen');
    }
  };
  const removeSelected = useCallback(() => {
    if (!selectedId) return;
    setElements((p) => p.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);
  const duplicateSelected = () => {
    if (!selected) return;
    const copy = { ...selected, id: uid(), xMm: selected.xMm + 3, yMm: selected.yMm + 3 } as LabelEl;
    setElements((p) => [...p, copy]);
    setSelectedId(copy.id);
  };

  // permite borrar el elemento seleccionado con Delete/Backspace, salvo que
  // el foco este en un campo de texto
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!selectedId) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        removeSelected();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, removeSelected]);

  const handleProductSelect = (id: string) => {
    setSelectedProductId(id);
    const product = products.find((p) => String(p.id) === id);
    if (!product) return;
    const spec = [
      product.width && product.length ? `${product.width}x${product.length}` : '',
      product.minQuantity ? ` x${product.minQuantity}u` : ''
    ].join('').trim();
    setElements((prev) => {
      const values = ['Contiene', product.name, spec || (product.material ?? '')];
      let ti = 0;
      const next = prev.map((e) => {
        if (e.type !== 'text') return e;
        const v = values[ti++];
        return v ? ({ ...e, text: v } as TextEl) : e;
      });
      const resin = mapMaterialToResin(product.material);
      return next.map((e) => (e.type === 'recycle' && resin ? { ...e, code: resin } : e));
    });
  };

  // --- etiquetas guardadas ------------------------------------------------
  const buildConfig = useCallback(
    () => ({
      labelWidthMm,
      labelHeightMm,
      quantity,
      showBorder,
      marginMm,
      gapMm,
      elements: elements.map((e) => {
        const clone: Record<string, unknown> = { ...e };
        delete clone.id;
        return clone;
      })
    }),
    [labelWidthMm, labelHeightMm, quantity, showBorder, marginMm, gapMm, elements]
  );

  const applyTemplate = (t: LabelTemplate) => {
    const cfg = t.payload || {};
    if (typeof cfg.labelWidthMm === 'number') setLabelWidthMm(clamp(Math.round(cfg.labelWidthMm), 20, 200));
    if (typeof cfg.labelHeightMm === 'number') setLabelHeightMm(clamp(Math.round(cfg.labelHeightMm), 12, 280));
    if (typeof cfg.quantity === 'number') setQuantity(clamp(Math.round(cfg.quantity), 1, 600));
    if (typeof cfg.showBorder === 'boolean') setShowBorder(cfg.showBorder);
    if (typeof cfg.marginMm === 'number') setMarginMm(clamp(Math.round(cfg.marginMm), 3, 20));
    if (typeof cfg.gapMm === 'number') setGapMm(clamp(Math.round(cfg.gapMm), 0, 15));
    if (Array.isArray(cfg.elements)) {
      setElements(
        (cfg.elements as LabelEl[])
          .filter((e) => e && (e.type === 'text' || e.type === 'image' || e.type === 'recycle' || e.type === 'symbol'))
          .map((e) => ({ ...e, id: uid() }))
      );
    }
    setSelectedId(null);
    setCurrentTemplateId(t.id);
    setTemplateName(t.name);
    setError('');
  };

  const saveTemplate = async (asNew: boolean) => {
    const name = templateName.trim();
    if (!name) {
      setError('Ingresa un nombre para guardar la etiqueta.');
      return;
    }
    try {
      setSavingTemplate(true);
      setError('');
      if (!asNew && currentTemplateId != null) {
        const { template } = await apiService.updateLabelTemplate(currentTemplateId, { name, payload: buildConfig() });
        setCurrentTemplateId(template.id);
      } else {
        const { template } = await apiService.createLabelTemplate(name, buildConfig());
        setCurrentTemplateId(template.id);
      }
      loadTemplates();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo guardar la etiqueta.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (t: LabelTemplate) => {
    if (!window.confirm(`¿Eliminar la etiqueta guardada "${t.name}"?`)) return;
    try {
      await apiService.deleteLabelTemplate(t.id);
      if (currentTemplateId === t.id) {
        setCurrentTemplateId(null);
        setTemplateName('');
      }
      loadTemplates();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo eliminar la etiqueta.');
    }
  };

  // --- layout / paginado ---------------------------------------------------
  const perPage = useMemo(() => {
    const cols = Math.max(1, Math.floor((A4_W - 2 * marginMm + gapMm) / (labelWidthMm + gapMm)));
    const rows = Math.max(1, Math.floor((A4_H - 2 * marginMm + gapMm) / (labelHeightMm + gapMm)));
    return { cols, rows, total: cols * rows };
  }, [labelWidthMm, labelHeightMm, marginMm, gapMm]);
  const pages = Math.max(1, Math.ceil(quantity / Math.max(1, perPage.total)));
  const fillPct = Math.min(100, Math.round(((perPage.total * labelWidthMm * labelHeightMm) / (A4_W * A4_H)) * 100));
  // baja margen y separacion al minimo razonable para imprimir, para entrar
  // con la mayor cantidad posible de etiquetas por hoja
  const optimizeSheet = () => {
    setMarginMm(5);
    setGapMm(2);
  };

  const buildPdfPayload = useCallback((firstPageOnly = false) => {
    const payloadElements = elements
      .filter((e) => (e.type === 'text' ? e.text.trim() : true))
      .map((e) => {
        if (e.type === 'text') {
          return { type: 'text', text: e.text.trim(), xMm: e.xMm, yMm: e.yMm, wMm: e.wMm, fontPt: e.fontPt, fontFamily: e.fontFamily || 'sans', bold: e.bold, align: e.align };
        }
        if (e.type === 'image') {
          return { type: 'image', src: e.src, xMm: e.xMm, yMm: e.yMm, wMm: e.wMm, hMm: e.hMm };
        }
        if (e.type === 'symbol') {
          return { type: 'symbol', icon: e.icon, xMm: e.xMm, yMm: e.yMm, wMm: e.wMm };
        }
        return { type: 'recycle', code: e.code, xMm: e.xMm, yMm: e.yMm, wMm: e.wMm };
      });

    return {
      labelWidthMm,
      labelHeightMm,
      quantity: firstPageOnly ? Math.min(quantity, perPage.total) : quantity,
      showBorder,
      marginMm,
      gapMm,
      elements: payloadElements
    };
  }, [elements, gapMm, labelHeightMm, labelWidthMm, marginMm, perPage.total, quantity, showBorder]);

  // La vista previa usa el PDF real del backend. Así fuentes, imágenes,
  // redondeos y posiciones son exactamente los mismos que al descargarlo.
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    const usable = elements.some((e) => (e.type === 'text' ? e.text.trim() : true));

    if (!usable) {
      setPdfPreviewUrl('');
      setPreviewError('Agregá al menos un elemento con contenido.');
      return () => { active = false; };
    }

    const timer = window.setTimeout(async () => {
      setIsPreviewLoading(true);
      setPreviewError('');
      try {
        const { blob } = await apiService.generateLabelsPdf(buildPdfPayload(true));
        objectUrl = window.URL.createObjectURL(blob);
        if (active) setPdfPreviewUrl(objectUrl);
        else window.URL.revokeObjectURL(objectUrl);
      } catch {
        if (active) setPreviewError('No se pudo actualizar la vista previa del PDF.');
      } finally {
        if (active) setIsPreviewLoading(false);
      }
    }, 600);

    return () => {
      active = false;
      window.clearTimeout(timer);
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [buildPdfPayload, elements]);

  const generate = async () => {
    const usable = elements.some((e) => (e.type === 'text' ? e.text.trim() : true));
    if (!usable) {
      setError('Agrega al menos un elemento con contenido.');
      return;
    }
    try {
      setIsGenerating(true);
      setError('');
      const { blob, filename } = await apiService.generateLabelsPdf(buildPdfPayload(false));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      let message = 'No se pudo generar el PDF de etiquetas.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.detail) message = String(parsed.detail);
        } catch {
          /* ignore */
        }
      } else if (data?.detail) {
        message = String(data.detail);
      }
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  // --- render de un elemento en el lienzo editable ------------------------
  const renderEl = (el: LabelEl) => {
    const isSel = el.id === selectedId;
    const { width, height } = elBox(el, pxPerMm);

    return (
      <div
        key={el.id}
        onPointerDown={(e) => onPointerDownEl(e, el, 'move')}
        style={{
          position: 'absolute',
          left: el.xMm * pxPerMm,
          top: el.yMm * pxPerMm,
          width,
          height,
          cursor: 'move',
          outline: isSel ? '1.5px solid #2563eb' : '1px dashed rgba(100,116,139,0.45)',
          outlineOffset: 1,
          display: 'flex',
          alignItems: 'flex-start',
          touchAction: 'none',
          userSelect: 'none'
        }}
      >
        <ElementInner el={el} pxPerMm={pxPerMm} />
        {isSel && (
          <span
            onPointerDown={(e) => onPointerDownEl(e, el, 'resize')}
            title="Arrastrar para agrandar"
            style={{
              position: 'absolute',
              right: -6,
              bottom: -6,
              width: 12,
              height: 12,
              borderRadius: 3,
              background: '#2563eb',
              border: '2px solid #fff',
              cursor: 'nwse-resize'
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Etiquetador</h1>
        <p className="text-sm text-slate-500">
          Diseña la etiqueta arrastrando y redimensionando los elementos. Genera una hoja A4 lista para imprimir y cortar.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
        {/* ---- editor ---- */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-2">
            <button onClick={addText} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
              <Type className="h-4 w-4" /> Texto
            </button>
            <button onClick={() => addLogo('polcen')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
              <ImageIcon className="h-4 w-4" /> Logo Polcen
            </button>
            <button onClick={() => addLogo('polcen-iso')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
              <ImageIcon className="h-4 w-4" /> Isotipo
            </button>
            <button onClick={() => { setReplaceTargetId(null); fileInputRef.current?.click(); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
              <ImageIcon className="h-4 w-4" /> Subir imagen
            </button>
            <button onClick={addRecycle} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
              <Recycle className="h-4 w-4" /> Símbolo reciclado
            </button>
            <div className="relative">
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) addSymbol(e.target.value as SymbolIcon);
                  e.target.value = '';
                }}
                className="appearance-none inline-flex items-center gap-1.5 pl-8 pr-6 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 cursor-pointer bg-white"
              >
                <option value="" disabled>Símbolo</option>
                {Object.entries(SYMBOL_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <AlertTriangle className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500" />
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onUploadImage} />
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => setZoom((z) => clamp(round1(z - 0.2), 0.4, 3))} className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="text-xs text-slate-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => clamp(round1(z + 0.2), 0.4, 3))} className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 overflow-auto">
            <p className="text-sm font-medium text-slate-700 mb-3">Diseño de la etiqueta</p>
            <div
              ref={canvasRef}
              onPointerDown={() => { setSelectedId(null); setTextEditorOpen(false); }}
              style={{
                position: 'relative',
                width: labelWidthMm * pxPerMm,
                height: labelHeightMm * pxPerMm,
                margin: '0 auto',
                background: '#fff',
                border: showBorder ? '1px solid #0f172a' : '1px solid #e2e8f0',
                boxShadow: '0 0 0 1px rgba(148,163,184,0.15)'
              }}
            >
              {elements.map(renderEl)}
            </div>
            <p className="text-center text-xs text-slate-400 mt-3">
              Etiqueta {labelWidthMm} × {labelHeightMm} mm · escala {Math.round(zoom * 100)}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 overflow-auto">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-700">Vista previa real del PDF</p>
              {isPreviewLoading && <span className="text-xs text-slate-500">Actualizando…</span>}
            </div>
            {previewError ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">{previewError}</div>
            ) : pdfPreviewUrl ? (
              <iframe
                title="Vista previa real del PDF de etiquetas"
                src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&view=FitH`}
                className={`h-[620px] w-full rounded border border-slate-200 bg-slate-100 transition-opacity ${isPreviewLoading ? 'opacity-60' : 'opacity-100'}`}
              />
            ) : (
              <div className="relative">
                <SheetPreview
                  elements={elements}
                  wMm={labelWidthMm}
                  hMm={labelHeightMm}
                  quantity={quantity}
                  border={showBorder}
                  cols={perPage.cols}
                  rows={perPage.rows}
                  marginMm={marginMm}
                  gapMm={gapMm}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm text-slate-600">Generando vista previa real…</div>
              </div>
            )}
          </div>

          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <button
            onClick={generate}
            disabled={isGenerating}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {isGenerating ? 'Generando...' : `Generar PDF (${quantity} etiquetas · ${pages} hoja${pages === 1 ? '' : 's'})`}
          </button>
        </div>

        {/* ---- panel lateral ---- */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Etiquetas guardadas</p>

            {templates.length === 0 ? (
              <p className="text-xs text-slate-400">Todavía no guardaste ninguna etiqueta.</p>
            ) : (
              <ul className="divide-y divide-slate-100 -mx-1 max-h-52 overflow-auto">
                {templates.map((t) => (
                  <li key={t.id} className={`flex items-center gap-2 px-1 py-1.5 rounded ${currentTemplateId === t.id ? 'bg-primary-50' : ''}`}>
                    <button
                      onClick={() => applyTemplate(t)}
                      className="flex-1 text-left text-sm text-slate-700 hover:text-primary-700 truncate"
                      title={`Cargar "${t.name}"`}
                    >
                      {t.name}
                    </button>
                    <button
                      onClick={() => deleteTemplate(t)}
                      title="Eliminar"
                      className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="pt-1 border-t border-slate-100 space-y-2">
              <input
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="Nombre de la etiqueta"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                {currentTemplateId != null && (
                  <button
                    onClick={() => saveTemplate(false)}
                    disabled={savingTemplate}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" /> Actualizar
                  </button>
                )}
                <button
                  onClick={() => saveTemplate(true)}
                  disabled={savingTemplate}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" /> {currentTemplateId != null ? 'Guardar como nueva' : 'Guardar'}
                </button>
                {currentTemplateId != null && (
                  <button
                    onClick={() => { setCurrentTemplateId(null); setTemplateName(''); }}
                    className="px-3 py-1.5 text-sm rounded-lg text-slate-500 hover:bg-slate-50"
                  >
                    Nueva
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-700">Hoja y etiqueta</p>
              <button onClick={optimizeSheet} className="text-xs text-primary-700 underline whitespace-nowrap">
                Maximizar aprovechamiento
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-slate-600">
                Ancho (mm)
                <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  value={labelWidthMm} onChange={(e) => setLabelWidthMm(normInt(e.target.value, 90, 20, 200))} />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Alto (mm)
                <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  value={labelHeightMm} onChange={(e) => setLabelHeightMm(normInt(e.target.value, 45, 12, 280))} />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Cantidad
                <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  value={quantity} onChange={(e) => setQuantity(normInt(e.target.value, 27, 1, 600))} />
              </label>
              <label className="flex items-end gap-2 text-xs font-medium text-slate-600 pb-1.5">
                <input type="checkbox" checked={showBorder} onChange={(e) => setShowBorder(e.target.checked)} />
                Borde de corte
              </label>
              <label className="text-xs font-medium text-slate-600">
                Margen de hoja (mm)
                <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  value={marginMm} onChange={(e) => setMarginMm(normInt(e.target.value, 8, 3, 20))} />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Separación (mm)
                <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  value={gapMm} onChange={(e) => setGapMm(normInt(e.target.value, 3, 0, 15))} />
              </label>
            </div>
            <p className="text-xs text-slate-500">
              {perPage.cols}×{perPage.rows} = {perPage.total} por hoja · {fillPct}% de la hoja aprovechado
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-700">Autocompletar desde producto</p>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={selectedProductId} onChange={(e) => handleProductSelect(e.target.value)}>
              <option value="">— Manual —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.isActive ? '' : ' (inactivo)'}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500">Rellena los textos y detecta el código de resina del material.</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Elemento seleccionado</p>
              {selected && (
                <div className="flex gap-1">
                  <button onClick={duplicateSelected} title="Duplicar" className="p-1.5 rounded border border-slate-300 hover:bg-slate-50">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={removeSelected} title="Eliminar" className="p-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            {!selected && <p className="text-xs text-slate-400">Hacé clic en un elemento del lienzo para editarlo.</p>}

            {selected && (
              <div className="space-y-3">
                {selected.type === 'text' && (
                  <>
                    <label className="block text-xs font-medium text-slate-600">
                      Texto
                      <input className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={selected.text}
                        onChange={(e) => patch(selected.id, { text: e.target.value })} />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="text-xs font-medium text-slate-600">
                        Tamaño (pt)
                        <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={selected.fontPt}
                          onChange={(e) => patch(selected.id, { fontPt: normInt(e.target.value, 12, 4, 72) })} />
                      </label>
                      <label className="text-xs font-medium text-slate-600">
                        Ancho caja (mm)
                        <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.wMm)}
                          onChange={(e) => patch(selected.id, { wMm: normInt(e.target.value, 50, 6, labelWidthMm + 40) })} />
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                        <input type="checkbox" checked={selected.bold} onChange={(e) => patch(selected.id, { bold: e.target.checked })} />
                        Negrita
                      </label>
                      <select className="border rounded px-2 py-1 text-sm" value={selected.align}
                        onChange={(e) => patch(selected.id, { align: e.target.value as Align })}>
                        <option value="left">Izquierda</option>
                        <option value="center">Centro</option>
                        <option value="right">Derecha</option>
                      </select>
                    </div>
                  </>
                )}

                {selected.type === 'image' && (
                  <>
                    <p className="text-xs text-slate-500">{selected.label}</p>
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-1">Isotipo</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(POLCEN_LOGOS).map(([key, meta]) => (
                          <button
                            key={key}
                            onClick={() => patch(selected.id, { src: key, ratio: meta.ratio, hMm: round1(selected.wMm / meta.ratio), label: meta.label })}
                            className={`px-2 py-1 text-xs rounded border ${selected.src === key ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-300 hover:bg-slate-50'}`}
                          >
                            {meta.label}
                          </button>
                        ))}
                        {customLogos.map((logo) => (
                          <button
                            key={logo.key}
                            onClick={() => patch(selected.id, { src: logo.url, ratio: logo.ratio, hMm: round1(selected.wMm / logo.ratio), label: logo.label })}
                            title={logo.label}
                            className={`p-0.5 rounded border ${selected.src === logo.url ? 'border-primary-500' : 'border-slate-300 hover:bg-slate-50'}`}
                          >
                            <img src={logo.url} alt={logo.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <label className="block text-xs font-medium text-slate-600">
                      Ancho (mm)
                      <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.wMm)}
                        onChange={(e) => {
                          const w = normInt(e.target.value, 24, 4, labelWidthMm + 20);
                          patch(selected.id, { wMm: w, hMm: round1(w / (selected.ratio || 1)) });
                        }} />
                    </label>
                    <button onClick={() => { setReplaceTargetId(selected.id); fileInputRef.current?.click(); }} className="text-xs text-primary-700 underline">
                      Reemplazar por otra imagen
                    </button>
                  </>
                )}

                {selected.type === 'recycle' && (
                  <>
                    <label className="block text-xs font-medium text-slate-600">
                      Código de resina
                      <select className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={selected.code}
                        onChange={(e) => patch(selected.id, { code: e.target.value })}>
                        {RESIN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                    <label className="block text-xs font-medium text-slate-600">
                      Tamaño (mm)
                      <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.wMm)}
                        onChange={(e) => patch(selected.id, { wMm: normInt(e.target.value, 14, 4, 120) })} />
                    </label>
                  </>
                )}

                {selected.type === 'symbol' && (
                  <>
                    <label className="block text-xs font-medium text-slate-600">
                      Símbolo
                      <select className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={selected.icon}
                        onChange={(e) => patch(selected.id, { icon: e.target.value as SymbolIcon })}>
                        {Object.entries(SYMBOL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </label>
                    <label className="block text-xs font-medium text-slate-600">
                      Tamaño (mm)
                      <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.wMm)}
                        onChange={(e) => patch(selected.id, { wMm: normInt(e.target.value, 16, 4, 120) })} />
                    </label>
                  </>
                )}

                <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100">
                  <label className="text-xs font-medium text-slate-600">
                    X (mm)
                    <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.xMm)}
                      onChange={(e) => patch(selected.id, { xMm: normInt(e.target.value, 0, -10, labelWidthMm + 20) })} />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Y (mm)
                    <input type="number" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={Math.round(selected.yMm)}
                      onChange={(e) => patch(selected.id, { yMm: normInt(e.target.value, 0, -10, labelHeightMm + 20) })} />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {textEditorOpen && selected?.type === 'text' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="text-editor-title"
          onPointerDown={() => setTextEditorOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white shadow-2xl"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="text-editor-title" className="font-semibold text-slate-900">Editar texto</h2>
                <p className="text-xs text-slate-500">Los cambios se aplican directamente en la etiqueta.</p>
              </div>
              <button type="button" onClick={() => setTextEditorOpen(false)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Cerrar">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <label className="block text-sm font-medium text-slate-700">
                Texto
                <input autoFocus className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" value={selected.text} onChange={(e) => patch(selected.id, { text: e.target.value })} />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm font-medium text-slate-700">
                  Tipografía
                  <select className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={selected.fontFamily || 'sans'} onChange={(e) => patch(selected.id, { fontFamily: e.target.value as FontFamily })}>
                    <option value="sans">Arial / Helvetica</option>
                    <option value="serif">Times</option>
                    <option value="mono">Courier</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Tamaño (pt)
                  <input type="number" min={4} max={72} className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={selected.fontPt} onChange={(e) => patch(selected.id, { fontPt: normInt(e.target.value, 12, 4, 72) })} />
                </label>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Ancho de la caja (mm)
                <input type="number" min={6} className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={Math.round(selected.wMm)} onChange={(e) => patch(selected.id, { wMm: normInt(e.target.value, 50, 6, labelWidthMm + 40) })} />
              </label>

              <div className="flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={selected.bold} onChange={(e) => patch(selected.id, { bold: e.target.checked })} />
                  Negrita
                </label>
                <label className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                  Alineación
                  <select className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" value={selected.align} onChange={(e) => patch(selected.id, { align: e.target.value as Align })}>
                    <option value="left">Izquierda</option>
                    <option value="center">Centro</option>
                    <option value="right">Derecha</option>
                  </select>
                </label>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={() => setTextEditorOpen(false)} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">Listo</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Etiquetas;
